import type { CatalogPiece } from "@/lib/api/api-client";
import type { Tonality } from "@/lib/music/tonality";

/**
 * Mise en forme des champs d'une pièce du catalogue.
 *
 * Reprise de `library-modal-utils.ts`, moins deux choses :
 *
 *  - `coverHueFromId` — la teinte hachée depuis l'identifiant. À 40 % de
 *    saturation et 18 % de luminosité, les 360 teintes rendaient le même gris,
 *    et un `id` n'a de toute façon aucun sens musical. La carte porte
 *    désormais la vraie silhouette du morceau (`silhouette.ts`) ;
 *  - `POPULARITY_FILTER_LABELS` — les libellés de seuil du filtre de
 *    notoriété. La notoriété **anti-corrèle** avec l'accessibilité
 *    (ρ = −0,12) : filtrer dessus renvoie donc surtout des morceaux
 *    injouables pour qui débute. Elle devient de la curation éditoriale
 *    (les sélections), pas un réglage à manœuvrer.
 *
 * Module pur : ni React, ni DOM.
 */

export type Difficulty = 1 | 2 | 3 | 4 | 5;

export const ALL_DIFFICULTIES: readonly Difficulty[] = [1, 2, 3, 4, 5];

export function isValidDifficulty(value: number | null): value is Difficulty {
  return value !== null && Number.isInteger(value) && value >= 1 && value <= 5;
}

/**
 * Notoriété du morceau, 1..5 — « est-ce que je le connais ? », pas « est-ce
 * qu'il est bon ». Elle vient du nombre de vues de la transcription sur
 * musescore.com, sur une échelle logarithmique posée à l'import.
 *
 * Conservée parce que le serveur la renvoie et que les sélections s'en servent ;
 * elle ne s'affiche plus sur les cartes.
 */
export type Popularity = 1 | 2 | 3 | 4 | 5;

export const POPULARITY_LABELS: Record<Popularity, string> = {
  1: "Confidentiel",
  2: "Peu connu",
  3: "Connu",
  4: "Très connu",
  5: "Incontournable",
};

export function isValidPopularity(value: number | null): value is Popularity {
  return value !== null && Number.isInteger(value) && value >= 1 && value <= 5;
}

/**
 * Le bucket de tempo affiché sous la valeur BPM (Lent / Modéré / …).
 */
export function tempoLabel(bpm: number): string {
  if (bpm < 60) {
    return "Lent";
  }

  if (bpm < 80) {
    return "Modéré";
  }

  if (bpm < 110) {
    return "Allant";
  }

  if (bpm < 140) {
    return "Rapide";
  }

  return "Très rapide";
}

export function formatDuration(durationSec: number | null): string | null {
  if (durationSec === null || durationSec <= 0) {
    return null;
  }

  const minutes = Math.floor(durationSec / 60);
  const seconds = durationSec % 60;

  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

/**
 * `CatalogPiece.tonality` tel que le serveur le sert : « C major », « D minor »,
 * « Ab major ». Le mode est en anglais, la tonique en notation internationale.
 */
const CATALOG_TONALITY_RE = /^([A-G][#b]{0,2}) (major|minor)$/;

/**
 * Lit la tonalité du catalogue vers le modèle du produit.
 *
 * # Pourquoi ce n'est pas affichable tel quel
 *
 * La chaîne est un champ de données, pas un libellé : elle est en anglais, et
 * la fiche était le seul écran du produit à l'imprimer brute — « C major » à
 * côté d'un aperçu qui dit « Do maj » et d'une palette d'accords qui dit
 * « Do · Mi · Sol ». Le rendu passe donc par `tonalityLabel()`, qui applique la
 * notation choisie par l'utilisateur.
 *
 * `null` sur toute chaîne qu'on ne sait pas lire — un mode exotique, une
 * tonique absente. L'appelant retombe alors sur la valeur brute : mieux vaut
 * « C major » qu'un vide, la donnée reste vraie même mal habillée.
 */
export function parseCatalogTonality(raw: string | null): Tonality | null {
  if (raw === null) {
    return null;
  }

  const match = CATALOG_TONALITY_RE.exec(raw.trim());

  if (match === null) {
    return null;
  }

  return { tonic: match[1], mode: match[2] as Tonality["mode"] };
}

export function pieceDisplayArtist(piece: CatalogPiece): string {
  return piece.composer ?? "Compositeur inconnu";
}

export function pieceDisplayGenre(piece: CatalogPiece): string {
  return piece.genre ?? "Sans genre";
}

/**
 * Les deux sorties MuseScore de l'écran vide du catalogue.
 *
 * Ici plutôt que dans le composant pour être testables sans DOM : ce sont des
 * URL construites à partir d'une saisie utilisateur, donc le seul endroit du
 * lot où une régression serait silencieuse — un paramètre mal encodé ne casse
 * rien à l'écran, il envoie juste sur la mauvaise page.
 */

/**
 * Le filtre « Piano » de la recherche MuseScore.com. Valeur vérifiée sur leur
 * URL publique : `?instrument=2` renvoie bien la page piano.
 */
const MUSESCORE_PIANO_INSTRUMENT = "2";

/** Page de téléchargement de l'éditeur, en français. */
export const MUSESCORE_STUDIO_URL = "https://musescore.org/fr/download";

/**
 * Recherche MuseScore.com pré-remplie. Sans requête — le vide vient alors des
 * filtres — on envoie sur le catalogue piano plutôt que sur une recherche vide,
 * qui n'aurait rien affiché.
 */
export function museScoreSearchUrl(query: string): string {
  const params = new URLSearchParams({
    instrument: MUSESCORE_PIANO_INSTRUMENT,
  });

  if (query !== "") {
    params.set("text", query);
  }

  return `https://musescore.com/sheetmusic?${params.toString()}`;
}
